/* Homepage hero slideshow. */
(() => {
  const selector = '[data-nl-hero-slideshow]';
  const controllers = window.__nlHeroSlideshowControllers || (window.__nlHeroSlideshowControllers = new Map());

  function init(root) {
    if (controllers.has(root)) return;
    const track = root.querySelector('.nl-hero-slideshow__track');
    if (!track) return;
    const slides = Array.from(track.children).filter((slide) => slide.matches('[data-hero-slide]'));
    if (!slides.length) return;
    const controls = root.querySelector('.nl-hero-slideshow__controls');
    const previous = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const pause = root.querySelector('[data-hero-pause]');
    const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
    const status = root.querySelector('[data-hero-status]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const removers = [];
    let current = Math.max(0, slides.findIndex((slide) => !slide.hidden));
    let available = [], autoTimer, expiryTimer, focusTimer, frame;
    let paused = false, hovered = false, focused = root.contains(document.activeElement), touch;
    let destroyed = false, lastWidth = root.getBoundingClientRect().width;

    const listen = (target, name, handler, options) => {
      if (!target) return;
      target.addEventListener(name, handler, options);
      removers.push(() => target.removeEventListener(name, handler, options));
    };
    const deadline = (slide) => {
      const raw = slide.getAttribute('data-hero-end');
      if (raw === null || raw === '') return Infinity;
      const value = Number(raw);
      return Number.isFinite(value) && value > 0 ? (value < 1e12 ? value * 1000 : value) : 0;
    };
    const label = (slide) => slide.getAttribute('aria-label') || `Slide ${slides.indexOf(slide) + 1}`;

    function updatePause() {
      if (!pause) return;
      const stopped = paused || reduced.matches;
      pause.setAttribute('aria-pressed', String(stopped));
      pause.setAttribute('aria-label', reduced.matches ? 'Automatic slideshow disabled by reduced motion' : stopped ? 'Resume slideshow' : 'Pause slideshow');
      pause.disabled = reduced.matches || available.length < 2;
      const text = pause.querySelector('[data-hero-pause-label]');
      if (text) text.textContent = stopped ? 'Play' : 'Pause';
      else pause.textContent = stopped ? 'Play' : 'Pause';
    }

    function scheduleAuto() {
      clearTimeout(autoTimer);
      updatePause();
      if (destroyed || paused || hovered || focused || reduced.matches || document.hidden || available.length < 2 || !root.isConnected) return;
      autoTimer = setTimeout(() => move(1, 'automatic'), 8000);
    }

    function fitSale() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (destroyed) return;
        const slide = slides[current];
        const inner = slide && !slide.hidden && slide.querySelector('.nl-hero-sale__inner');
        if (!inner) return;
        const base = parseFloat(getComputedStyle(root).getPropertyValue('--nl-hero-base-height')) || 600;
        const height = Math.ceil(Math.max(base, inner.scrollHeight));
        if (Math.abs(track.getBoundingClientRect().height - height) > 1) root.style.setProperty('--nl-hero-height', `${height}px`);
      });
    }

    function show(index, reason = 'manual') {
      if (!available.includes(index)) return;
      const prior = slides[current];
      const displacedFocus = prior && prior !== slides[index] && prior.contains(document.activeElement);
      current = index;
      slides.forEach((slide, number) => {
        const inactive = number !== current || !available.includes(number);
        slide.hidden = inactive;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
      });
      dots.forEach((dot) => {
        const index = Number(dot.dataset.heroDot);
        dot.setAttribute('aria-pressed', String(index === current));
        dot.disabled = !available.includes(index);
      });
      if (status) status.textContent = reason === 'manual' ? `${label(slides[current])}. Slide ${available.indexOf(current) + 1} of ${available.length}.` : '';
      if (displacedFocus) {
        const target = slides[current].querySelector('a[href],button:not([disabled]),[tabindex="0"]');
        if (target) target.focus({ preventScroll: true });
        else { root.setAttribute('tabindex', '-1'); root.focus({ preventScroll: true }); }
      }
      fitSale();
      scheduleAuto();
    }

    function refreshAvailability() {
      clearTimeout(expiryTimer);
      const now = Date.now();
      available = slides.map((slide, index) => deadline(slide) > now ? index : -1).filter((index) => index >= 0);
      root.hidden = available.length === 0;
      if (controls) controls.hidden = available.length < 2;
      if (previous) previous.disabled = available.length < 2;
      if (next) next.disabled = available.length < 2;
      if (!available.length) { clearTimeout(autoTimer); return; }
      show(available.includes(current) ? current : available[0], 'expiry');
      const ends = available.map((index) => deadline(slides[index])).filter(Number.isFinite);
      if (ends.length) expiryTimer = setTimeout(refreshAvailability, Math.min(Math.max(Math.min(...ends) - now, 1), 86400000));
    }

    function move(direction, reason = 'manual') {
      refreshAvailability();
      if (available.length < 2) return;
      const position = available.indexOf(current);
      show(available[(position + direction + available.length) % available.length], reason);
    }

    listen(previous, 'click', () => move(-1));
    listen(next, 'click', () => move(1));
    dots.forEach((dot) => listen(dot, 'click', () => { refreshAvailability(); show(Number(dot.dataset.heroDot)); }));
    listen(pause, 'click', () => { if (!reduced.matches) { paused = !paused; scheduleAuto(); } });
    listen(root, 'mouseenter', () => { hovered = true; scheduleAuto(); });
    listen(root, 'mouseleave', () => { hovered = false; scheduleAuto(); });
    listen(root, 'focusin', () => { focused = true; scheduleAuto(); });
    listen(root, 'focusout', () => {
      clearTimeout(focusTimer);
      focusTimer = setTimeout(() => { focused = root.contains(document.activeElement); scheduleAuto(); }, 0);
    });
    listen(root, 'keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (event.target.closest('form,input,textarea,select,[contenteditable=""],[contenteditable="true"]')) return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    listen(root, 'pointerdown', (event) => {
      if (event.pointerType !== 'touch' || event.target.closest('a,button,summary,form,input,textarea,select,[contenteditable]')) return;
      touch = { x: event.clientX, y: event.clientY, id: event.pointerId };
    });
    listen(root, 'pointerup', (event) => {
      if (!touch || touch.id !== event.pointerId) return;
      const x = event.clientX - touch.x, y = event.clientY - touch.y;
      touch = null;
      if (Math.abs(x) > 40 && Math.abs(x) > Math.abs(y) * 1.25) move(x > 0 ? -1 : 1);
    });
    listen(root, 'pointercancel', () => { touch = null; });
    listen(document, 'visibilitychange', () => { refreshAvailability(); scheduleAuto(); });
    listen(window, 'resize', () => {
      const width = root.getBoundingClientRect().width;
      if (Math.abs(width - lastWidth) > 1) { lastWidth = width; root.style.removeProperty('--nl-hero-height'); fitSale(); }
    });
    listen(root, 'load', fitSale, true);
    const motionChange = () => scheduleAuto();
    if (reduced.addEventListener) listen(reduced, 'change', motionChange);
    else { reduced.addListener(motionChange); removers.push(() => reduced.removeListener(motionChange)); }
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(fitSale) : null;
    const saleInner = root.querySelector('.nl-hero-sale__inner');
    if (observer && saleInner) observer.observe(saleInner);
    controllers.set(root, () => {
      destroyed = true;
      clearTimeout(autoTimer); clearTimeout(expiryTimer); clearTimeout(focusTimer); cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      removers.forEach((remove) => remove());
      controllers.delete(root);
    });
    refreshAvailability();
  }

  function scan(scope = document) {
    if (scope.matches && scope.matches(selector)) init(scope);
    scope.querySelectorAll(selector).forEach(init);
  }
  if (!window.__nlHeroSlideshowLifecycle) {
    window.__nlHeroSlideshowLifecycle = true;
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      Array.from(controllers).forEach(([root, destroy]) => { if (event.target === root || event.target.contains(root)) destroy(); });
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  }
  scan();
})();

/* Use the store's existing Instafeed account; no invented social posts. */
(() => {
  const feed = document.getElementById('insta-feed');
  if (!feed) return;
  const source = 'https://cdn.nfcube.com/instafeed-f8158416fdfeaf298e7dc4d41961043a.js';
  let nearby = false;
  const load = () => {
    if (!nearby || !window.Shopify?.customerPrivacy?.marketingAllowed()) return;
    if ([...document.scripts].some(script => script.src.split('?')[0] === source)) return;
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    document.body.appendChild(script);
  };
  document.addEventListener('visitorConsentCollected', load);
  document.addEventListener('instafeedAppLoaded', () => {
    const fallback = document.querySelector('[data-nl-insta-fallback]');
    if (feed.children.length && fallback) fallback.hidden = true;
  });
  if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], load);
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      nearby = true;
      load();
      observer.disconnect();
    }, { rootMargin: '500px' });
    observer.observe(feed);
  } else { nearby = true; load(); }
})();
